import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { Alert, AlertService, AlertType, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FreeRepairFiltersResponse, Pagination, PaginatedFreeRepairResponse, FreeRepair } from '../../models/response.model';
import { map } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { Router } from '@angular/router';
import { SharedDialogComponent } from '../../../../shared/components/shared-dialog/shared-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CustomerAssignService } from '../../../../shared/services/customer-assign.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';

@Component({
  selector: 'app-free-repair',
  templateUrl: './free-repair.component.html',
  styleUrl: './free-repair.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FreeRepairComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<FreeRepair>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  public router = inject(Router);
  public dialog = inject(MatDialog);
  public customerAssignService = inject(CustomerAssignService);

  codesList: string[] = [];
  descriptionList: string[] = [];
  dealerRetreadNoList: string[] = [];

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  freeRepairs: FreeRepair[] = [];
  plants: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  currentFreeRepair: FreeRepair;
  productSaveDisabled = true;
  freeRepairBreadcrumbs = [
    { link: '/home/dashboard', icon: 'home' },
    { label: this.translationService.translate('productDataText') },
    { link: '/home/productData/freeRepair', label: this.translationService.translate('freeRepairText') }
  ];
  additionalColumns = [
    { property: 'freeRepairCode', headerKey: this.translationService.translate('freeRepairCodeText') },
    { property: 'freeRepairDesc', headerKey: this.translationService.translate('freeRepairDescText') }
  ];

  tableConfig: TableConfig<FreeRepair> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {},
    onRowClick: (elm: FreeRepair) => {
      this.router.navigateByUrl(`/home/productData/freeRepair/freeRepairSetup/${elm.freeRepairTablePk}`, {
        state: { code: elm.code, description: elm.description, totalRepairs: elm.total, isCopyAs: false }
      });
    }
  };

  fetcher: PaginatorFetcher<FreeRepair> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getFreeRepair({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: PaginatedFreeRepairResponse) => {
        this.freeRepairs = response.data.freeRepairTables || [];
        this.pagination = response.data.pagination;
        this.updateDisplayedRowsForm(this.freeRepairs);
        return { items: this.freeRepairs, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      code: [[]],
      description: [[]]
    });
    this.filterFormBackend = this.fb.group({
      code: [[]],
      description: [[]]
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.productDataService.getFreeRepairFilters().subscribe((response: FreeRepairFiltersResponse) => {
      this.codesList = response.data.freeRepairCodes || [];
      this.descriptionList = response.data.freeRepairDescriptions || [];
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        repairCode: { cell: (row) => row.code, header: this.translationService.translate('codeText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') }
      };
      this.cdr.markForCheck();
    });
    this.updateDropdownLists();
  }

  assignToCustomer(item: FreeRepair): void {
    this.currentFreeRepair = item;

    const navigationData = {
      breadcrumbLinks: this.freeRepairBreadcrumbs,
      currentDataObject: this.currentFreeRepair,
      additionalColumns: this.additionalColumns,
      searchUri: 'searchFreeRepairs',
      assignUri: 'assignFreeRepairsToCustomers',
      service: 'backOfficeUrl'
    };

    this.customerAssignService.setNavigationData(navigationData);
    this.router.navigate(['/home/productData/customer-assign', this.currentFreeRepair.freeRepairTablePk]);
  }

  deleteFreeRepair(item: FreeRepair) {
    const dialogRef = this.dialog.open(SharedDialogComponent, {
      width: '550px',
      data: {
        title: this.translationService.translate('deleteText') + ' ' + this.translationService.translate('freeRepairText'),
        message:
          this.translationService.translate('deleteCamSpecModalContentLine1Text') +
          ' ' +
          this.translationService.translate('freeRepairText') +
          ' ' +
          item.code +
          '?. ' +
          this.translationService.translate('deleteCamSpecModalContentLine2Text'),
        buttonText: this.translationService.translate('deleteText'),
        cancelButtonText: this.translationService.translate('cancelText')
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Call API service to delete the record
        this.productDataService.deleteFreeRepair(item.freeRepairTablePk).subscribe({
          next: () => {
            // Remove deleted code from local codesList so filters/dropdowns stay in sync
            this.codesList = (this.codesList || []).filter((code) => code !== item.code);
            this.descriptionList = (this.descriptionList || []).filter((description) => description !== item.description);
            this.alertService.showAlert({
              content:
                this.translationService.translate('freeRepairText') + ' ' + this.translationService.translate('deletedSuccessfullyText'),
              type: AlertType.success
            } as Alert);
            // Refresh data after successful deletion
            this.tableRef.resetFilters();
          },
          error: (error) => {
            this.alertService.showAlert({
              content: error?.error?.message || this.translationService.translate('errorOccurred'),
              type: AlertType.critical
            } as Alert);
          }
        });
      }
    });
  }

  copyFreeRepair(item: FreeRepair) {
    this.router.navigateByUrl(`/home/productData/freeRepair/freeRepairSetup/${item.freeRepairTablePk}`, {
      state: { code: '', description: '', totalRepairs: item.total, isCopyAs: true }
    });
  }

  updateDropdownLists(): void {
    this.dealerRetreadNoList = [...this.store.selectSnapshot(ProductsState.products).rollPartNumbers];
  }

  createRowForm(row: FreeRepair): FormGroup {
    return this.fb.group({
      repairCode: [row.code],
      description: [row.description]
    });
  }

  private updateDisplayedRowsForm(rows: FreeRepair[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: FreeRepair, controlName: keyof FreeRepair): FormControl {
    const index = this.freeRepairs.findIndex((row) => row.code === item.code);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset({
      code: this.codesList,
      description: this.descriptionList
    });
  }

  createFreeRepair(): void {
    if (this.productSaveDisabled) {
      return;
    }
    this.router.navigateByUrl(`/home/productData/freeRepair/freeRepairSetup/`, {
      state: { code: '', description: '', totalRepairs: '', isCopyAs: false }
    });
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      code: this.filterForm.get('code')?.value || '',
      description: this.filterForm.get('description')?.value || ''
    });
  }

  protected isDeletable(item: FreeRepair): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return !item.deletable && roles.includes('PRODUCT_SETUP_EDIT');
  }

  protected readonly FEATURE_FLAGS = FEATURE_FLAGS;
}
